import { useEffect, useReducer } from 'react';
import { X, Delete } from 'lucide-react';
import { cn } from '@jeraisonne/ui';
import {
  calcReducer,
  INITIAL_CALC_STATE,
  type CalcAction,
  type CalcOperator,
} from '@/lib/calculator/calculator-engine';

type Key =
  | { kind: 'action'; label: string; action: CalcAction; variant: 'fn' | 'op'; icon?: 'backspace' }
  | { kind: 'digit'; label: string }
  | { kind: 'decimal' }
  | { kind: 'equals' };

const KEYS: Key[] = [
  { kind: 'action', label: 'AC', action: { type: 'clear' }, variant: 'fn' },
  { kind: 'action', label: '⌫', action: { type: 'backspace' }, variant: 'fn', icon: 'backspace' },
  { kind: 'action', label: '%', action: { type: 'percent' }, variant: 'fn' },
  { kind: 'action', label: '÷', action: { type: 'operator', value: '÷' }, variant: 'op' },
  { kind: 'digit', label: '7' },
  { kind: 'digit', label: '8' },
  { kind: 'digit', label: '9' },
  { kind: 'action', label: '×', action: { type: 'operator', value: '×' }, variant: 'op' },
  { kind: 'digit', label: '4' },
  { kind: 'digit', label: '5' },
  { kind: 'digit', label: '6' },
  { kind: 'action', label: '−', action: { type: 'operator', value: '-' }, variant: 'op' },
  { kind: 'digit', label: '1' },
  { kind: 'digit', label: '2' },
  { kind: 'digit', label: '3' },
  { kind: 'action', label: '+', action: { type: 'operator', value: '+' }, variant: 'op' },
  { kind: 'action', label: '±', action: { type: 'negate' }, variant: 'fn' },
  { kind: 'digit', label: '0' },
  { kind: 'decimal' },
  { kind: 'equals' },
];

const KEY_BY_CHAR: Record<string, CalcAction> = {
  '+': { type: 'operator', value: '+' },
  '-': { type: 'operator', value: '-' },
  '*': { type: 'operator', value: '×' },
  '/': { type: 'operator', value: '÷' },
  '%': { type: 'percent' },
  '.': { type: 'decimal' },
  ',': { type: 'decimal' },
  Enter: { type: 'equals' },
  '=': { type: 'equals' },
  Backspace: { type: 'backspace' },
  Escape: { type: 'clear' },
};

const OP_SYMBOL: Record<CalcOperator, string> = { '+': '+', '-': '−', '×': '×', '÷': '÷' };

export function CalculatorModal({ onClose }: { onClose: () => void }) {
  const [state, dispatch] = useReducer(calcReducer, INITIAL_CALC_STATE);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (e.key >= '0' && e.key <= '9') {
        dispatch({ type: 'digit', value: e.key });
        return;
      }
      const action = KEY_BY_CHAR[e.key];
      if (action) {
        e.preventDefault();
        dispatch(action);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[60] flex flex-col justify-end bg-black/40 backdrop-blur-sm sm:items-center sm:justify-center sm:p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Calculatrice"
    >
      <div
        className="pb-safe ocean:bg-[#061433] flex max-h-[92vh] w-full flex-col overflow-hidden rounded-t-3xl bg-white shadow-2xl sm:max-w-sm sm:rounded-3xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="border-ink-100 flex shrink-0 items-center justify-between border-b px-5 py-3.5">
          <h2 className="font-display text-ink-900 text-base font-bold">Calculatrice</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fermer"
            className="text-ink-400 hover:bg-ink-100 hover:text-ink-700 flex min-h-11 min-w-11 items-center justify-center rounded-xl transition-colors active:scale-[0.98]"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Affichage */}
        <div className="bg-ink-50 flex min-h-[5.5rem] flex-col items-end justify-center gap-1 px-5 py-4">
          <div className="text-ink-400 h-4 font-mono text-sm">
            {state.operand !== null && state.operator !== null
              ? `${state.operand} ${OP_SYMBOL[state.operator]}`
              : ''}
          </div>
          <div className="font-display text-ink-900 max-w-full truncate text-4xl font-bold tabular-nums">
            {state.display}
          </div>
        </div>

        {/* Clavier */}
        <div className="grid grid-cols-4 gap-2 p-3">
          {KEYS.map((key, i) => {
            if (key.kind === 'digit') {
              return (
                <CalcButton key={i} onClick={() => dispatch({ type: 'digit', value: key.label })}>
                  {key.label}
                </CalcButton>
              );
            }
            if (key.kind === 'decimal') {
              return (
                <CalcButton key={i} onClick={() => dispatch({ type: 'decimal' })}>
                  ,
                </CalcButton>
              );
            }
            if (key.kind === 'equals') {
              return (
                <CalcButton key={i} variant="equals" onClick={() => dispatch({ type: 'equals' })}>
                  =
                </CalcButton>
              );
            }
            return (
              <CalcButton key={i} variant={key.variant} onClick={() => dispatch(key.action)}>
                {key.icon === 'backspace' ? <Delete className="h-5 w-5" /> : key.label}
              </CalcButton>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function CalcButton({
  children,
  onClick,
  variant = 'digit',
}: {
  children: React.ReactNode;
  onClick: () => void;
  variant?: 'digit' | 'op' | 'fn' | 'equals';
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'font-display flex h-14 items-center justify-center rounded-2xl text-xl font-bold transition-colors active:scale-[0.95]',
        variant === 'digit' && 'bg-ink-100 text-ink-900 hover:bg-ink-200',
        variant === 'fn' && 'bg-ink-200/70 text-ink-700 hover:bg-ink-200',
        variant === 'op' && 'bg-primary-100 text-primary-700 hover:bg-primary-200',
        variant === 'equals' && 'bg-primary-600 hover:bg-primary-700 text-white',
      )}
    >
      {children}
    </button>
  );
}
